import React, { useState } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle,
  DialogClose
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Download, Expand, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';
import { config } from '@/lib/config';
import { motion } from 'framer-motion';

interface ImageGenerationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImageGenerated: (imageUrls: string[], prompt: string) => void;
}

const ImageGenerationModal: React.FC<ImageGenerationModalProps> = ({
  isOpen,
  onClose,
  onImageGenerated
}) => {
  const [prompt, setPrompt] = useState('');
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [numImages, setNumImages] = useState('1');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!prompt.trim()) {
      setError('Please enter a prompt');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      console.log("Sending image generation request to API...");
      const response = await fetch('/api/generate-image', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        // Include cache-control headers to prevent caching issues
        cache: 'no-cache',
        body: JSON.stringify({
          prompt,
          aspect_ratio: aspectRatio,
          n: parseInt(numImages, 10),
          prompt_optimizer: true,
          model: 'image-01',
          response_format: 'url'
        }),
      });
      
      console.log("API response status:", response.status);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate image');
      }
      
      const data = await response.json();
      console.log("Image generation response:", data);
      
      if (data.base_resp?.status_code !== 0) {
        throw new Error(data.base_resp?.status_msg || 'Image generation failed');
      }
      
      // Extract image URLs from response according to MiniMax API structure
      const imageUrls = data.data?.image_urls || [];
      
      if (imageUrls.length === 0) {
        throw new Error('No images were generated');
      }
      
      // Logging success info
      if (data.metadata) {
        console.log(`Generation stats: Success: ${data.metadata.success_count}, Failed: ${data.metadata.failed_count}`);
      }
      
      console.log('Successfully generated images, URLs:', imageUrls);
      
      // Pass the generated images back to the parent component
      onImageGenerated(imageUrls, prompt);
      
      // Close the modal
      onClose();
      
      // Show success message
      toast.success(`Generated ${imageUrls.length} image${imageUrls.length > 1 ? 's' : ''}`);
      
    } catch (err) {
      console.error('Image generation error:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate image');
      toast.error('Image generation failed');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[500px] border-none overflow-hidden glass-card">
        {/* Enhanced glassmorphism background with better transparency */}
        <div className="absolute inset-0 rounded-2xl bg-[#0d0d0e]/10 backdrop-blur-[12px] z-[1]"></div>
        
        {/* Subtle gradient border */}
        <div className="absolute inset-0 rounded-2xl overflow-hidden z-[2]" style={{ padding: '1px' }}>
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/10 to-neon-purple/20 opacity-50"></div>
        </div>
        
        {/* 3D shadow effect */}
        <div className="absolute -bottom-4 inset-x-8 h-[20px] bg-black/30 blur-xl rounded-full z-[-5]"></div>
        
        <div className="relative z-[5]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-neon-purple">
              <img src="/public/pixel-title.png" alt="Generate using Obsidian Pixel" className="h-10 w-auto" />
            </DialogTitle>
            <DialogDescription className="text-white/70">
              Enter a detailed prompt to generate AI images using Obsidian Pixel.
            </DialogDescription>
            
            {/* Close button */}
            <DialogClose className="absolute right-2 top-2 rounded-full p-1.5 text-white/70 hover:bg-white/10 transition-colors">
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </DialogClose>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Glowing textarea container */}
            <div className="relative">
              <div className="absolute -inset-0.5 rounded-md bg-neon-purple/20 opacity-70 blur-sm"></div>
              <Textarea
                placeholder="Describe the image you want to generate in detail... (e.g., 'A futuristic cityscape with neon lights and flying cars, digital art style')"
                className="min-h-[120px] resize-none relative bg-black/25 backdrop-blur-md border-neon-purple/30 focus:border-neon-purple/50 text-white"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                disabled={isLoading}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-white/90">Aspect Ratio</label>
                <Select
                  value={aspectRatio}
                  onValueChange={setAspectRatio}
                  disabled={isLoading}
                >
                  <SelectTrigger className="bg-black/25 backdrop-blur-md border-neon-purple/30 text-white">
                    <SelectValue placeholder="Aspect Ratio" />
                  </SelectTrigger>
                  <SelectContent className="bg-black/80 backdrop-blur-md border-neon-purple/30">
                    <SelectItem value="1:1">Square (1:1)</SelectItem>
                    <SelectItem value="16:9">Landscape (16:9)</SelectItem>
                    <SelectItem value="9:16">Portrait (9:16)</SelectItem>
                    <SelectItem value="4:3">Standard (4:3)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-white/90">Number of Images</label>
                <Select
                  value={numImages}
                  onValueChange={setNumImages}
                  disabled={isLoading}
                >
                  <SelectTrigger className="bg-black/25 backdrop-blur-md border-neon-purple/30 text-white">
                    <SelectValue placeholder="Number of Images" />
                  </SelectTrigger>
                  <SelectContent className="bg-black/80 backdrop-blur-md border-neon-purple/30">
                    <SelectItem value="1">1 Image</SelectItem>
                    <SelectItem value="2">2 Images</SelectItem>
                    <SelectItem value="3">3 Images</SelectItem>
                    <SelectItem value="4">4 Images</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {error && (
              <div className="text-sm text-red-500 bg-red-500/15 backdrop-blur-md p-2 rounded-md border border-red-500/20">
                {error}
              </div>
            )}
            
            {/* Tips section with glassmorphic style */}
            <div className="mt-4 p-3 rounded-md bg-neon-purple/10 backdrop-blur-md border border-neon-purple/20 text-xs text-white/80">
              <h4 className="font-semibold mb-1 text-neon-purple">Tips for better results:</h4>
              <ul className="list-disc pl-4 space-y-1">
                <li>Be specific about art style (e.g., "digital art", "realistic photo", "abstract painting")</li>
                <li>Include details about lighting, colors, and composition</li>
                <li>Mention specific moods or atmospheres you want to convey</li>
              </ul>
            </div>
            
            <DialogFooter className="flex-col sm:flex-row gap-2 mt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={onClose}
                disabled={isLoading}
                className="w-full sm:w-auto border-neon-purple/30 text-white bg-black/25 backdrop-blur-md hover:bg-neon-purple/20"
              >
                Cancel
              </Button>
              
              <motion.div 
                className="w-full sm:w-auto"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              >
                <Button 
                  type="submit" 
                  className="w-full bg-neon-purple hover:bg-neon-purple/90 text-white"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-4 w-4" />
                      Generate Images
                    </>
                  )}
                </Button>
              </motion.div>
            </DialogFooter>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImageGenerationModal; 